import { useCallback, useEffect, useMemo } from 'react';
import { ArtifactModel, ArtifactType, ConfigModel, useGetArtifacts } from '@bymason/controller-api-hooks';
import { useAuth } from '@bymason-private/auth-provider';
import { ControllerFontType } from '@bymason-private/controller-v1-theme-provider';
import { GridSelectionModel, Text } from '@bymason-private/legacy-pattern-components';
import { useToast } from '@bymason-private/controller-shared-widgets';
import SelectArtifact from './select-artifact';

export default function AppsStep({ config, onSelectApps }: AppsStepProps) {
  const { authToken = '', customerId } = useAuth();
  const [setToastMessage, setToastSeverity] = useToast();
  const [getArtifacts, artifacts, artifactsError, artifactsBusy] = useGetArtifacts(authToken);

  const selectedApps: GridSelectionModel = useMemo(() => {
    if (artifacts) {
      return (
        config?.apps?.map((app) => {
          const appId =
            artifacts.find((art) => art.name === app.name && art.version === `${app.version_code}`)
              ?.composite || '';
          return appId;
        }) || []
      );
    }
    return [];
  }, [config?.apps, artifacts]);

  const handleUploadError = useCallback(
    (message: string) => {
      setToastMessage(message);
      setToastSeverity('error');
    },
    [setToastMessage, setToastSeverity]
  );

  const handleUploadSuccess = useCallback(() => {
    if (customerId) {
      getArtifacts({ customerId, type: ArtifactType.Apk });
    }
  }, [customerId, getArtifacts]);

  useEffect(() => {
    if (customerId) {
      getArtifacts({ customerId, type: ArtifactType.Apk });
    }
  }, [customerId, getArtifacts]);

  useEffect(() => {
    if (artifactsError) {
      setToastMessage(artifactsError);
      setToastSeverity('error');
    }
  }, [artifactsError, setToastMessage, setToastSeverity]);

  return (
    <div>
      <SelectArtifact
        header={
          <div>
            <Text variant={ControllerFontType.Heading300B}>Select your apps (apk).{` `}</Text>
            <Text variant={ControllerFontType.Body400R}>
              Get started with an app. Add your custom APK file or use our{' '}
              <a
                href='https://github.com/MasonAmerica/Mason-Starter-Pack/raw/master/apps-v2/iHeartLives-Demo-V2.apk'
                download
              >
                sample app
              </a>
              .
            </Text>
          </div>
        }
        onUploadSuccess={handleUploadSuccess}
        onError={handleUploadError}
        artifacts={artifacts || undefined}
        artifactsBusy={artifactsBusy}
        selectionType='apk'
        selected={selectedApps}
        onSelect={onSelectApps}
        showDropzone={!artifacts?.length}
        isRowSelectable={(params) => {
          return !selectedApps.filter((appId) => {
            // Don't disable selected app row
            if ((appId as string).includes(`${params.row.name}:${params.row.version}`)) {
              return false;
            }
            // Do disable selected app's other versions
            return (appId as string).includes(params.row.name);
          }).length;
        }}
      />
    </div>
  );
}

interface AppsStepProps {
  config: ConfigModel;
  onSelectApps: (selectedArtifacts: ArtifactModel[]) => void;
}
